import { Component, Input, OnInit, OnChanges, OnDestroy, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatasetService } from 'app/main/components/dataset/services/dataset.service';
import { WebSocketService } from 'app/services/websocket.service';
import { ISocketEvent } from 'app/interfaces';
import { ActualTripParticipantLogsDialogComponent } from './participant-logs-dialog.component';

interface IParticipantRow {
	id: string;
	trip_booking_id: string;
	trip_booking_code: string;
	trip_booking_date: string;
	description: string;
	age_range_code: string;
	nation_code: string;
	lang_code: string;
	presence: number;
	last_presence_checked_at: string | null;
	note: string | null;
	trip_booking_participant_id: string;
	_updating?: boolean;
}

interface IListResponse {
	items: IParticipantRow[];
	meta: { last: boolean; page: number; perPage: number; total: number };
}

@Component({
	selector: 'actual-trip-participants-tab',
	templateUrl: './participants-tab.component.html',
	styleUrls: ['./participants-tab.component.scss']
})
export class ActualTripParticipantsTabComponent implements OnInit, OnChanges, OnDestroy {

	@Input() actualTripId: string;
	@Input() canEdit = false;
	@Output() presenceChanged = new EventEmitter<void>();

	private _unsubscribeAll = new Subject<void>();
	private _socketHandler = (event: ISocketEvent) => this.onSocketEvent(event);

	loading = false;
	bulkLoading = false;
	rows: IParticipantRow[] = [];
	displayedColumns: string[] = [];
	selection = new SelectionModel<string>(true, []);

	constructor(
		private datasetService: DatasetService,
		private snackBar: MatSnackBar,
		private webSocketService: WebSocketService,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.recomputeColumns();
		if (this.actualTripId) this.load();
		if (this.webSocketService && this.webSocketService.ioSocket) {
			this.webSocketService.ioSocket.on('channel-event', this._socketHandler);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.canEdit) this.recomputeColumns();
		if (changes.actualTripId && !changes.actualTripId.firstChange) {
			this.selection.clear();
			this.load();
		}
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next();
		this._unsubscribeAll.complete();
		if (this.webSocketService && this.webSocketService.ioSocket) {
			this.webSocketService.ioSocket.off('channel-event', this._socketHandler);
		}
	}

	private recomputeColumns(): void {
		const base = ['description', 'trip_booking_code', 'age_range_code', 'presence', 'last_presence_checked_at', 'note', 'logs'];
		this.displayedColumns = this.canEdit ? ['select', ...base] : base;
	}

	hasLogs(row: IParticipantRow): boolean {
		return !!row.id && row.id.indexOf('B-') !== 0;
	}

	openLogs(row: IParticipantRow): void {
		if (!this.hasLogs(row)) return;
		this.dialog.open(ActualTripParticipantLogsDialogComponent, {
			data: { participantId: row.id, participantDescription: row.description },
			minWidth: 520,
			maxWidth: '90vw'
		});
	}

	private onSocketEvent(event: ISocketEvent): void {
		if (!event || !event.data) return;
		if (event.data.datasetCode !== 'actual_trip_participants') return;
		if (event.data.payloadData && event.data.payloadData.actual_trip_id !== this.actualTripId) return;
		this.load(true);
	}

	load(silent = false): void {
		if (!this.actualTripId) return;
		if (!silent) this.loading = true;
		this.datasetService.post<IListResponse>('/dataset/actual_trip_participants/command/list', {
			actual_trip_id: this.actualTripId,
			perPage: 500
		})
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: res => {
					const items = (res && res.items) ? res.items : [];
					this.rows = items;
					this.loading = false;
					// keep only still-visible IDs in selection
					const visibleIds = new Set(this.rows.map(r => r.id));
					this.selection.selected
						.filter(id => !visibleIds.has(id))
						.forEach(id => this.selection.deselect(id));
				},
				error: () => {
					this.loading = false;
					this.snackBar.open('Errore caricamento partecipanti', 'OK', { duration: 4000 });
				}
			});
	}

	togglePresence(row: IParticipantRow): void {
		if (!this.canEdit || row._updating) return;
		const previous = row.presence;
		const next = previous == 1 ? 0 : 1;
		row.presence = next;
		row._updating = true;
		this.datasetService.post<IParticipantRow>('/dataset/actual_trip_participants/command/update_presence', {
			id: row.id,
			actual_trip_id: this.actualTripId,
			presence: next
		})
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: updated => {
					row._updating = false;
					if (updated && updated.id) {
						const oldId = row.id;
						row.id = updated.id;
						row.last_presence_checked_at = updated.last_presence_checked_at;
						if (this.selection.isSelected(oldId)) {
							this.selection.deselect(oldId);
							this.selection.select(updated.id);
						}
					}
					this.presenceChanged.emit();
				},
				error: () => {
					row.presence = previous;
					row._updating = false;
					this.snackBar.open('Errore aggiornamento presenza', 'OK', { duration: 4000 });
				}
			});
	}

	isAllSelected(): boolean {
		return this.rows.length > 0 && this.selection.selected.length === this.rows.length;
	}

	masterToggle(): void {
		if (this.isAllSelected()) {
			this.selection.clear();
		} else {
			this.rows.forEach(r => this.selection.select(r.id));
		}
	}

	bulkUpdate(presence: 0 | 1): void {
		if (!this.canEdit || this.bulkLoading) return;
		const ids = this.selection.selected;
		if (!ids.length) return;
		this.bulkLoading = true;
		const payload: any = { actual_trip_id: this.actualTripId };
		if (presence === 1) payload.present_participant_ids = ids;
		else payload.absent_participant_ids = ids;
		this.datasetService.post<{ success: boolean; result: { affected: number } }>(
			'/dataset/actual_trip_participants/command/mass_update_presence',
			payload
		)
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: res => {
					this.bulkLoading = false;
					this.selection.clear();
					this.snackBar.open(
						`Aggiornati ${res && res.result ? res.result.affected : 0} partecipanti`,
						'OK',
						{ duration: 3000 }
					);
					this.load(true);
					this.presenceChanged.emit();
				},
				error: () => {
					this.bulkLoading = false;
					this.snackBar.open('Errore aggiornamento massivo', 'OK', { duration: 4000 });
				}
			});
	}

	trackById(_index: number, row: IParticipantRow): string {
		return row.id;
	}
}
